import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { MenuIcon, XIcon } from 'lucide-react';
import { Logo } from '../ui/Logo';
import { ThemeToggle } from '../ui/ThemeToggle';
import { ButtonLink } from '../ui/ButtonLink';
import { LanguageSwitcher } from '../ui/LanguageSwitcher';
import { useMarketplace } from '../../contexts/MarketplaceContext';
import { useLanguage } from '../../contexts/LanguageContext';
import type { TranslationKey } from '../../data/translations';
import { spring, stagger, rise } from '../../utils/motion';
const links: {
  to: {
    pathname: string;
    hash?: string;
  };
  key: TranslationKey;
}[] = [{
  to: {
    pathname: '/',
    hash: '#how'
  },
  key: 'nav.how'
}, {
  to: {
    pathname: '/',
    hash: '#performers'
  },
  key: 'nav.performers'
}, {
  to: {
    pathname: '/',
    hash: '#organisers'
  },
  key: 'nav.organisers'
}, {
  to: {
    pathname: '/pricing'
  },
  key: 'nav.pricing'
}];
export function LandingNav() {
  const {
    role
  } = useMarketplace();
  const {
    t
  } = useLanguage();
  const [hovered, setHovered] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const signIn = role === 'organiser' ? '/o/events' : '/p/discover';
  return <header className="sticky top-3 z-50 px-3" data-id="element-368" data-mp="8rodfd0xiyjj">
      <div className="mx-auto flex h-16 max-w-6xl items-center gap-2 rounded-full border border-line bg-bg/90 pl-3 pr-2 backdrop-blur-md" data-id="element-369" data-mp="doesbx0zzo3d">
        <Link to="/" aria-label="Encorza home" className="rounded-full pr-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand" data-id="element-370" data-mp="11f115qittcef">
          <Logo data-id="element-371" data-mp="rozacqz39fvt" />
        </Link>
        <nav aria-label="Primary" className="ml-4 hidden items-center lg:flex" onMouseLeave={() => setHovered(null)} data-id="element-372" data-mp="rnob94gl8wb1">
          {links.map(l => <Link key={l.key} to={l.to} onMouseEnter={() => setHovered(l.key)} className="relative whitespace-nowrap rounded-full px-4 py-2 text-sm font-bold text-ink-2 transition-colors duration-150 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand" data-id="element-373" data-mp="wlg2rxdbmgep">
              {hovered === l.key && <motion.span layoutId="nav-hover" transition={spring.snappy} className="absolute inset-0 rounded-full bg-surface-2" data-id="element-374" data-mp="1tywdl6b4eu24" />}
              <span className="relative" data-id="element-375" data-mp="1l0kox9tte6cb">{t(l.key)}</span>
            </Link>)}
        </nav>
        <div className="ml-auto flex items-center gap-1" data-id="element-376" data-mp="1g0y4ogfkjpu0">
          <LanguageSwitcher id="landing" className="hidden sm:inline-flex" data-id="element-377" data-mp="1g2izm75spase" />
          <ThemeToggle data-id="element-378" data-mp="1b9pu3h2i5yf9" />
          <Link to={signIn} className="hidden whitespace-nowrap rounded-full px-4 py-2 text-sm font-bold text-ink-2 transition-colors duration-150 hover:text-ink sm:block" data-id="element-379" data-mp="1b8enl9hoackg">
            {t('nav.signIn')}
          </Link>
          <ButtonLink to="/start" variant="accent" size="sm" className="hidden h-11 whitespace-nowrap px-5 sm:inline-flex" data-id="element-380" data-mp="xaqeeywywtv8">
            {t('nav.getStarted')}
          </ButtonLink>
          <button type="button" onClick={() => setOpen(true)} aria-label="Open menu" className="grid h-11 w-11 place-items-center rounded-full text-ink transition-colors duration-150 hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand lg:hidden" data-id="element-381" data-mp="sdq7ymw7hp3l">
            <MenuIcon className="h-5 w-5" data-id="element-382" data-mp="3hz05dwhxdt57" />
          </button>
        </div>
      </div>

      <AnimatePresence data-id="element-383" data-mp="2l90ys7lc54p9">
        {open && <motion.div className="fixed inset-0 z-[60] flex flex-col bg-bg px-6 pb-8 pt-5 lg:hidden" initial={{
        opacity: 0,
        y: -16
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -16,
        transition: {
          duration: 0.16
        }
      }} transition={spring.smooth} role="dialog" aria-modal="true" aria-label="Menu" data-id="element-384" data-mp="2la2744ayvd3a">
            <div className="flex items-center justify-between" data-id="element-385" data-mp="21ifg2vzyxg13">
              <Logo data-id="element-386" data-mp="26ijnlwiwf0f6" />
              <div className="flex items-center gap-2" data-id="element-387" data-mp="26hs1dcb6yf1k">
                <LanguageSwitcher id="landing-mobile" data-id="element-388" data-mp="d0ci48yhknll" />
                <button type="button" onClick={() => setOpen(false)} aria-label="Close menu" className="grid h-11 w-11 place-items-center rounded-full transition-colors duration-150 hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand" data-id="element-389" data-mp="cyrrgufalot0">
                  <XIcon className="h-6 w-6" data-id="element-390" data-mp="vq2doya2pdek" />
                </button>
              </div>
            </div>
            <motion.nav variants={stagger} initial="hidden" animate="show" className="mt-12 flex flex-col gap-2" aria-label="Mobile" data-id="element-391" data-mp="qrr4mprlr6xe">
              {[...links, {
            to: {
              pathname: signIn
            },
            key: 'nav.signIn' as TranslationKey
          }].map(l => <motion.div key={l.key} variants={rise} data-id="element-392" data-mp="36jf1kl6m8zg7">
                  <Link to={l.to} onClick={() => setOpen(false)} className="block py-2 text-[32px] font-bold tracking-[-0.015em]" data-id="element-393" data-mp="36jx41zjvg9wk">
                    {t(l.key)}
                  </Link>
                </motion.div>)}
            </motion.nav>
            <div className="mt-auto" data-id="element-394" data-mp="2wf8a5ayj9hfz">
              <ButtonLink to="/start" variant="accent" size="lg" className="w-full" onClick={() => setOpen(false)} data-id="element-395" data-mp="2we6s2mj3nuor">
                {t('nav.getStarted')}
              </ButtonLink>
            </div>
          </motion.div>}
      </AnimatePresence>
    </header>;
}